import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import { RootState } from "store/reducers";
import { refreshToken, getUserInfo } from "store/user/thunks";
import { useInterval } from "./useInterval";
import rollbar from "helpers/rollbar";

/**
 * Hook to periodically refresh the user's authentication token and information. (SHOULD ONLY BE USED ONCE, in the DashboardLayout)
 * @param intervalMinutes How often to check token expiration in minutes (default: 15)
 * @param expirationBufferMinutes Minutes before actual expiration to refresh (default: 5)
 */
export const useTokenRefreshInterval = (
    intervalMinutes = 15,
    expirationBufferMinutes = 5
) => {
    const dispatch = useDispatch<AppDispatch>();
    const ticket = useSelector((state: RootState) => state.user.ticket);

    const refreshUserSession = useCallback(async () => {
        try {
            // Check if token exists
            if (!ticket) {
                return false;
            }

            let tokenRefreshed = false;

            // Check if token is expired or about to expire

            const currentTime = Math.floor(Date.now() / 1000);
            const expiryTime = Math.floor(
                new Date(ticket[".expires"]).getTime() / 1000
            );
            const bufferTime = expirationBufferMinutes * 60;

            if (currentTime + bufferTime >= expiryTime) {
                // Only refresh token if it's about to expire
                await dispatch(refreshToken());
                tokenRefreshed = true;
            }

            // Always refresh user info
            await dispatch(getUserInfo());

            return tokenRefreshed;
        } catch (error) {
            rollbar.error("Failed to refresh user session", error);
            return false;
        }
    }, [dispatch, ticket, expirationBufferMinutes]);

    // Convert minutes to milliseconds for the useInterval hook
    const intervalMs = intervalMinutes * 60 * 1000;

    const { initialize: startTokenRefreshInterval } = useInterval(
        refreshUserSession,
        intervalMs,
        {
            // Run on first mount (equivalent to optional initial refresh)
            runImmediately: false,
            // No predelay needed
            predelay: 0
        }
    );

    return {
        startTokenRefreshInterval
    };
};
